import React, { useState } from 'react';
import { Box, Button, Container, Grid } from '@mui/joy';

import i18n from '@/i18n'

import { Loading } from '@/components/Loading';
import { useParams } from 'react-router-dom';

import { useHostname } from '@/contexts';
import { Img } from '@/components/Img';
import { HBox } from '@/components/HBox';
import { Link } from 'react-router-dom';
import { Link as JoyLink } from '@mui/joy';
import { AppleLogo, Money, SpotifyLogo, TidalLogo } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useResource } from '@/hooks';
import { UserView } from '@/components/UserView';
import { Do, Empty, ForEach } from '@/components/Syntax';
import { ReleaseCard } from '@/components/ReleaseCard';
import { Typography } from '@mui/joy';
import { useManager } from '@/hooks';
import LoadingPage from '@/pages/loading';
import ErrorPage from '@/pages/error';

export function ArtistView({ artistId }) {
  const manager = useManager()
  const { data: artist, isLoading, error, refetch } = useResource({
    queryKey: [manager?.type, manager?.slug, 'artist', artistId],
    path: `/${manager?.type}/${manager?.slug}/artist/${artistId}`,
    enabled: !!manager
  })

  const { data: releasesData, isLoading: isLoadingReleases, error: errorReleases, refetch: refetchReleases } = useResource({
    queryKey: [manager?.type, manager?.slug, 'artist', artistId, 'releases'],
    path: `/${manager?.type}/${manager?.slug}/artist/${artistId}/releases`,
    enabled: !!manager
  })

  if (error) {
    return (
      <ErrorPage onTryAgainClick={() => refetch()} />
    ) 
  }

  if (isLoading) {
    return (
      <LoadingPage />
    )
  }

  if (artist) {
    return (
      <Container>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
          <Typography level="h1">{artist.name}</Typography>
          <Button
            component={Link}
            to={`edit`}
            variant="outlined"
          >{i18n.t('edit')}</Button>
          <Img src={artist.image_url} style={{ marginTop: 1, width: '120pt' }} />
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} className="links">
            {artist.spotify_id && (
              <JoyLink target="_blank" component={Link} to={`https://open.spotify.com/artist/${artist.spotify_id}`}>
                <SpotifyLogo weight="bold" size={28} />&nbsp;<Typography>Spotify</Typography>
              </JoyLink>
            )}
            {artist.apple_music_id && (
              <JoyLink component={Link} to={`https://music.apple.com/artist/${artist.apple_music_id}`}>
                <AppleLogo />&nbsp;<Typography>Apple Music</Typography>
              </JoyLink>
            )}
            {artist.tidal_id && (
              <JoyLink component={Link} to={`https://tidal.com/browse/artist/${artist.tidal_id}`}>
                <TidalLogo />&nbsp;<Typography>Tidal</Typography>
              </JoyLink>
            )}
            {/*artist.deezer_id && (
              <JoyLink component={Link} to={`https://deezer.com/en/artist/${artist.deezer_id}`}>
                <DeezerLogo />&nbsp;<Typography>Deezer</Typography>
              </JoyLink>
            )*/}
          </Box>
          <Typography level="h2">{i18n.t('users')}</Typography>
          <HBox>
            <ForEach collection={artist.members}>
              <Do>
                {member => (
                  <UserView key={member.id} user={member.user} />
                )}
              </Do>
              <Empty>
                <Typography fontStyle="italic">Artist has no users</Typography>
              </Empty>
            </ForEach>
          </HBox>
          <Typography level="h2">{i18n.t('releases')}</Typography>
          <Grid container spacing={2}>
            <ForEach collection={releasesData?.results}>
              <Do>
                {release => (
                  <Grid item key={release.id} sm={8} md={8}> 
                    <ReleaseCard
                      component={Link}
                      to={`../../release/${release.id}`}
                      {...release}
                    />
                  </Grid>
                )}
              </Do>
              <Empty>
                <Typography fontStyle="italic">Artist has not yet uploaded any releases</Typography>
              </Empty>
            </ForEach>
          </Grid>
        </Box>
      </Container>
    )
  } else {
    return (
      <LoadingPage />
    )
  }
}

export default function ArtistPage() {
  const { artistId } = useParams()

  return (
    <React.Suspense fallback={<LoadingPage />}>
      <ArtistView artistId={artistId} />
    </React.Suspense>
  )
}